import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx'

/**
 * Strip markdown formatting from text for plain export
 */
function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^[\u2500-\u257F\u2580-\u259F\u2501=]{2,}$/gm, '')
    .replace(/^━+$/gm, '')
    .replace(/^---+$/gm, '')
    .replace(/^===+$/gm, '')
    .replace(/\s*—\s*/g, ' ')
    .replace(/\s*–\s*/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Parse markdown text into docx Paragraph objects
 */
function parseMarkdownToDocx(content: string): Paragraph[] {
  const lines = content.split('\n')
  const paragraphs: Paragraph[] = []

  for (const line of lines) {
    const trimmed = line.trim()

    // Skip divider lines
    if (/^[━─=\-_*]{2,}$/.test(trimmed)) continue
    if (trimmed === '') {
      paragraphs.push(new Paragraph({ text: '' }))
      continue
    }

    // H1
    if (/^# /.test(line)) {
      paragraphs.push(new Paragraph({
        text: line.replace(/^# /, ''),
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 },
      }))
      continue
    }

    // H2
    if (/^## /.test(line)) {
      paragraphs.push(new Paragraph({
        text: line.replace(/^## /, ''),
        heading: HeadingLevel.HEADING_2,
        spacing: { after: 120 },
      }))
      continue
    }

    // H3
    if (/^### /.test(line)) {
      paragraphs.push(new Paragraph({
        text: line.replace(/^### /, ''),
        heading: HeadingLevel.HEADING_3,
        spacing: { after: 80 },
      }))
      continue
    }

    // Bullet points
    if (/^[-*•] /.test(trimmed)) {
      const bulletText = trimmed.replace(/^[-*•] /, '')
      const runs = parseBoldRuns(bulletText)
      paragraphs.push(new Paragraph({
        children: runs,
        bullet: { level: 0 },
        spacing: { after: 60 },
      }))
      continue
    }

    // Regular paragraph (with bold support)
    const runs = parseBoldRuns(trimmed)
    paragraphs.push(new Paragraph({
      children: runs,
      spacing: { after: 80 },
    }))
  }

  return paragraphs
}

/**
 * Parse inline bold **text** into TextRun array
 */
function parseBoldRuns(text: string): TextRun[] {
  const parts = text.split(/(\*\*.*?\*\*)/)
  return parts.map(part => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return new TextRun({ text: part.slice(2, -2), bold: true, size: 22 })
    }
    return new TextRun({ text: part, size: 22 })
  })
}

/**
 * Download a blob as a file
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Export a single section as a real .docx file
 */
export async function exportSectionToWord(label: string, content: string): Promise<void> {
  try {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            text: label,
            heading: HeadingLevel.TITLE,
            spacing: { after: 400 },
          }),
          ...parseMarkdownToDocx(content),
        ],
      }],
    })

    const blob = await Packer.toBlob(doc)
    downloadBlob(blob, `Searchline - ${label}.docx`)
  } catch (err) {
    console.error('[export] docx error:', err)
    // Fallback to plain text
    const blob = new Blob([stripMarkdown(content)], { type: 'text/plain;charset=utf-8' })
    downloadBlob(blob, `Searchline - ${label}.txt`)
  }
}

/**
 * Export all sections as a single real .docx file
 */
export async function exportAllToWord(sections: { label: string; content: string }[]): Promise<void> {
  try {
    const validSections = sections.filter(s => s.content && s.content.trim().length > 10)
    if (!validSections.length) return

    const allChildren = validSections.flatMap((section, idx) => [
      ...(idx > 0 ? [new Paragraph({ text: '', pageBreakBefore: true })] : []),
      new Paragraph({
        text: section.label,
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 300 },
      }),
      ...parseMarkdownToDocx(section.content),
    ])

    const doc = new Document({
      sections: [{
        properties: {},
        children: allChildren,
      }],
    })

    const blob = await Packer.toBlob(doc)
    downloadBlob(blob, `Searchline - All Outputs.docx`)
  } catch (err) {
    console.error('[export] docx error:', err)
    const combined = sections
      .filter(s => s.content && s.content.trim().length > 10)
      .map(s => `=== ${s.label} ===\n\n${stripMarkdown(s.content)}`)
      .join('\n\n\n')
    const blob = new Blob([combined], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Searchline - All Outputs.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
}
